import { makeAutoObservable, runInAction } from "mobx";
import {
  cloudStorage,
  isCloudStorageSupported
} from "@telegram-apps/sdk";

const STORAGE_KEY = "suggestions";

interface SuggestionsState {
  suggestions: string[];
  loaded: boolean;
}

class SuggestionsStoreClass {
  private _suggestions: string[] = [];
  private _loaded = false;
  private _loading = false;
  private _cachedState: SuggestionsState;
  private listeners = new Set<() => void>();

  constructor() {
    makeAutoObservable(this);
    this.loadFromLocalStorage();
    this._cachedState = this.buildState();
  }

  private loadFromLocalStorage(): void {
    if (!isCloudStorageSupported()) {
      const stored = localStorage[STORAGE_KEY] ?? "";
      this._suggestions = stored ? stored.split(",") : [];
      this._loaded = true;
    }
  }

  private buildState(): SuggestionsState {
    return {
      suggestions: this._suggestions,
      loaded: this._loaded
    };
  }

  get suggestions(): string[] {
    return this._suggestions;
  }

  get loaded(): boolean {
    return this._loaded;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getState = (): SuggestionsState => this._cachedState;

  private notify(): void {
    this._cachedState = this.buildState();
    this.listeners.forEach((l) => l());
  }

  async load(): Promise<void> {
    if (this._loaded || this._loading) return;

    this._loading = true;

    try {
      if (isCloudStorageSupported()) {
        const stored = await cloudStorage.getItem(STORAGE_KEY);
        runInAction(() => {
          this._suggestions = stored ? stored.split(",") : [];
          this._loaded = true;
          this.notify();
        });
      } else {
        runInAction(() => {
          this._loaded = true;
        });
      }
    } finally {
      runInAction(() => {
        this._loading = false;
      });
    }
  }

  addSuggestion(serviceType: string): void {
    runInAction(() => {
      const filtered = this._suggestions.filter((s) => s !== serviceType);
      this._suggestions = [serviceType, ...filtered].slice(0, 5);
      this.save();
      this.notify();
    });
  }

  private save(): void {
    const value = this._suggestions.join(",");
    if (isCloudStorageSupported()) {
      void cloudStorage.setItem(STORAGE_KEY, value);
    } else {
      localStorage[STORAGE_KEY] = value;
    }
  }
}

export const SuggestionsStore = new SuggestionsStoreClass();

